import { createAsyncThunk } from "@reduxjs/toolkit";

import * as productsAPI from "../../../api/products";
import { IProductID } from "../../../models/nominals";
import { IProductShippingMethod } from "../../../models/shipping";

/**
 * Parameters for loading shipping method.
 */
interface LoadShippingMethodParams {
    productID: IProductID;
    postCode: string;
}

/**
 * Load the shipping method for a product and location.
 *
 * This thunk fetches available shipping methods for a product at a specific postal code,
 * and returns the first (default) method, or null if no methods are available.
 *
 * @param {LoadShippingMethodParams} params - The shipping method loading parameters
 * @param {IProductID} params.productID - The product to get shipping methods for
 * @param {string} params.postCode - The postal code for shipping destination
 *
 * @returns {Promise<IProductShippingMethod | null>} Promise resolving to the default shipping method or null
 *
 * @example
 * // Load shipping method when location changes
 * dispatch(loadShippingMethod({
 *     productID: 123,
 *     postCode: "90210"
 * }));
 *
 * @sideEffects
 * - Updates `shippingMethod` state with the fetched method via extraReducers
 */
export const loadShippingMethod = createAsyncThunk(
    "configurator/loadShippingMethod",
    async (
        params: LoadShippingMethodParams,
    ): Promise<IProductShippingMethod | null> => {
        const resp = await productsAPI.getProductShippingMethods(
            params.productID,
            params.postCode,
        );
        const defaultMethod = resp.length > 0 ? resp[0] : null;
        return defaultMethod;
    },
);
